import { api, ApiError, NetworkError } from './api';
import { addOp, db, listOps, markSynced, saveCapture, type LocalEntry, type OutboxOp } from './store';

/**
 * Outbox sync. Every capture is written to IndexedDB first, then sent to POST /api/sync.
 * The server ingests idempotently by client-generated id, so retries, lost responses and
 * several tabs flushing the same outbox can never duplicate an entry. A Web Lock makes one
 * tab flush at a time; BroadcastChannel tells the other tabs to refresh.
 */

export interface SyncState {
  online: boolean;
  pending: number;
  failed: OutboxOp[];
  syncing: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
}

let state: SyncState = { online: navigator.onLine, pending: 0, failed: [], syncing: false, lastSyncAt: null, lastError: null };
const listeners = new Set<(s: SyncState) => void>();
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('trpg-sync') : null;
let backoff = 0;
let timer: number | undefined;

function emit(patch: Partial<SyncState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l(state);
}

export function onSync(fn: (s: SyncState) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

/** Other parts of the UI listen for this to reload local entries / server data. */
export const syncEvents = new EventTarget();

async function refreshCounts() {
  const ops = await listOps();
  emit({ pending: ops.filter((o) => !o.failed).length, failed: ops.filter((o) => o.failed) });
}

let seqBase = Number(localStorage.getItem('trpg.seq') ?? '0');
function nextSeq(): number {
  seqBase = Math.max(seqBase + 1, Date.now() * 10);
  try {
    localStorage.setItem('trpg.seq', String(seqBase));
  } catch {
    /* ignore */
  }
  return seqBase;
}

export function deviceId(): string {
  let id = localStorage.getItem('trpg.device');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('trpg.device', id);
  }
  return id;
}

export async function captureEntry(p: { campaignId: string; sessionId: string; text: string; visibility: 'public' | 'gm_only' | null; inGameTime: string | null }): Promise<LocalEntry> {
  const now = new Date();
  const entry: LocalEntry = {
    id: crypto.randomUUID(),
    revisionId: crypto.randomUUID(),
    campaignId: p.campaignId,
    sessionId: p.sessionId,
    text: p.text,
    capturedAt: now.toISOString(),
    capturedTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    clientSeq: nextSeq(),
    visibility: p.visibility,
    inGameTime: p.inGameTime,
    synced: false,
  };
  const op: OutboxOp = {
    opId: crypto.randomUUID(),
    type: 'createEntry',
    campaignId: p.campaignId,
    createdAt: entry.capturedAt,
    attempts: 0,
    payload: {
      id: entry.id,
      revisionId: entry.revisionId,
      campaignId: entry.campaignId,
      sessionId: entry.sessionId,
      text: entry.text,
      capturedAt: entry.capturedAt,
      capturedTz: entry.capturedTz,
      capturedOffsetMin: -now.getTimezoneOffset(),
      clientSeq: entry.clientSeq,
      deviceId: deviceId(),
      visibility: entry.visibility,
      inGameTime: entry.inGameTime,
    },
  };
  await saveCapture(entry, op);
  changed();
  return entry;
}

export async function queueOp(type: OutboxOp['type'], campaignId: string, payload: any): Promise<void> {
  await addOp({ opId: crypto.randomUUID(), type, campaignId, payload, createdAt: new Date().toISOString(), attempts: 0 });
  changed();
}

export async function createSessionOffline(campaignId: string, title: string): Promise<string> {
  const id = crypto.randomUUID();
  await queueOp('createSession', campaignId, { id, campaignId, title, playedOn: new Date().toLocaleDateString('en-CA') });
  return id;
}

function changed() {
  channel?.postMessage('outbox');
  syncEvents.dispatchEvent(new Event('outbox'));
  void refreshCounts();
  void flush();
}

/** Discard a failed operation (explicit user choice; nothing is ever dropped silently). */
export async function discardOp(seq: number): Promise<void> {
  await (await db()).delete('outbox', seq);
  changed();
}

export async function retryFailed(): Promise<void> {
  const d = await db();
  for (const op of await d.getAll('outbox')) if (op.failed) await d.put('outbox', { ...op, failed: false, error: undefined });
  changed();
}

async function withLock(fn: () => Promise<void>): Promise<void> {
  const locks = (navigator as any).locks;
  if (locks?.request) {
    await locks.request('trpg-sync', { ifAvailable: true }, async (lock: unknown) => {
      if (lock) await fn();
    });
  } else if (!state.syncing) await fn();
}

export async function flush(): Promise<void> {
  await withLock(async () => {
    emit({ syncing: true });
    try {
      for (;;) {
        const ops = (await listOps()).filter((o) => !o.failed).slice(0, 50);
        if (ops.length === 0) break;
        const res = await api<{ results: any[] }>('/api/sync', { body: { ops: ops.map(({ opId, type, payload }) => ({ opId, type, payload })) } });
        const d = await db();
        let stop = false;
        for (const r of res.results) {
          const op = ops.find((o) => o.opId === r.opId);
          if (!op) continue;
          if (r.ok) {
            await d.delete('outbox', op.seq!);
            if (op.type === 'createEntry') await markSynced(op.payload.id);
          } else if (r.retryable) {
            await d.put('outbox', { ...op, attempts: op.attempts + 1, error: r.error });
            stop = true; // keep order: later ops may depend on this one
            break;
          } else {
            await d.put('outbox', { ...op, attempts: op.attempts + 1, error: r.error, failed: true });
          }
        }
        backoff = 0;
        emit({ online: true, lastSyncAt: new Date().toISOString(), lastError: null });
        syncEvents.dispatchEvent(new Event('synced'));
        channel?.postMessage('synced');
        if (stop) {
          schedule(5000);
          break;
        }
      }
    } catch (err) {
      if (err instanceof NetworkError) {
        backoff = Math.min(60_000, backoff ? backoff * 2 : 2000);
        emit({ online: false, lastError: err.message });
        schedule(backoff);
      } else if (err instanceof ApiError && err.status === 401) {
        emit({ lastError: 'Sign in to sync' });
      } else {
        emit({ lastError: String((err as Error)?.message ?? err) });
        schedule(15_000);
      }
    } finally {
      emit({ syncing: false });
      await refreshCounts();
    }
  });
}

function schedule(ms: number) {
  clearTimeout(timer);
  timer = window.setTimeout(() => void flush(), ms);
}

export function startSync(): void {
  channel?.addEventListener('message', (e) => {
    if (e.data === 'outbox' || e.data === 'synced') {
      void refreshCounts();
      syncEvents.dispatchEvent(new Event(e.data));
    }
  });
  window.addEventListener('online', () => {
    emit({ online: true });
    void flush();
  });
  window.addEventListener('offline', () => emit({ online: false }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flush();
  });
  window.setInterval(() => void flush(), 20_000);
  void refreshCounts();
  void flush();
}
